
import { useLocation } from "react-router-dom";

export default function NotFound() {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en');
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-900 to-indigo-900 flex items-center justify-center px-4 overflow-hidden relative">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-32 h-32 text-amber-400/10 animate-float">
          <i className="ri-star-line text-6xl"></i>
        </div>
        <div className="absolute top-40 right-20 w-24 h-24 text-purple-300/10 animate-float-delayed">
          <i className="ri-moon-line text-5xl"></i>
        </div>
        <div className="absolute bottom-40 left-1/4 w-28 h-28 text-pink-300/10 animate-float">
          <i className="ri-planet-line text-5xl"></i>
        </div>
        <div className="absolute top-1/3 right-1/3 w-20 h-20 text-amber-300/10 animate-float-delayed">
          <i className="ri-sparkling-2-line text-4xl"></i>
        </div>
        <div className="absolute bottom-20 right-10 w-24 h-24 text-purple-400/10 animate-float">
          <i className="ri-compass-3-line text-5xl"></i>
        </div>
      </div>

      {/* Large 404 background text */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
        <span className="text-[20rem] md:text-[30rem] font-black text-white/5">404</span>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center max-w-2xl mx-auto">
        {/* Icon */}
        <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl">
          <i className="ri-compass-discover-line text-5xl text-white"></i>
        </div>

        {/* Title */}
        <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-4">
          {isEnglish ? 'Lost in the Stars' : 'Pasiklydai Žvaigždėse'}
        </h1>

        {/* Subtitle */}
        <p className="text-xl text-white/80 mb-4">
          {isEnglish 
            ? "This page doesn't exist in our universe" 
            : 'Šis puslapis neegzistuoja mūsų visatoje'}
        </p>

        {/* Path display */}
        <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 inline-block mb-8">
          <code className="text-purple-300 text-sm font-mono">{location.pathname}</code>
        </div>

        {/* Description */}
        <p className="text-white/60 mb-10 max-w-md mx-auto">
          {isEnglish 
            ? "The stars have guided you to an unknown path. Let's get you back on track." 
            : 'Žvaigždės nuvedė tave nežinomu keliu. Grįžkime į teisingą kelią.'}
        </p>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href={isEnglish ? '/en' : '/'}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all shadow-xl flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line text-xl"></i>
            {isEnglish ? 'Back to Home' : 'Grįžti į Pradžią'}
          </a>
          <a
            href={isEnglish ? '/en/contact' : '/contact'}
            className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold hover:bg-white/20 transition-all border border-white/20 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-line text-xl"></i>
            {isEnglish ? 'Contact Us' : 'Susisiekti'}
          </a>
        </div>

        {/* Fun fact */}
        <div className="mt-16 bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
          <div className="flex items-center justify-center gap-2 text-amber-400 mb-3">
            <i className="ri-lightbulb-line text-xl"></i>
            <span className="font-semibold text-sm uppercase tracking-wider">
              {isEnglish ? 'Did you know?' : 'Ar žinojai?'}
            </span>
          </div>
          <p className="text-white/70 text-sm">
            {isEnglish 
              ? 'Even the stars sometimes lose their way. But every journey leads to self-discovery.' 
              : 'Net žvaigždės kartais pasiklysta. Bet kiekviena kelionė veda į savęs atradimą.'}
          </p>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(-5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
